import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Check, ChevronDown, EyeOff, GripVertical, LayoutTemplate } from 'lucide-react';
import { WIDGET_BY_ID, WidgetId, ZONES, ZONE_LABEL, Zone } from './dashboardLayout';
import { ZONE_SHORT, insertionIndex, insertionLineY, readDragPayload, setDragPayload } from './dashboardEditing';

/**
 * Anpassen-Modus am PC: jeder Baustein steckt in einem Rahmen mit Griff,
 * Pfeilen, Zonenmenue und „Ausblenden"; jede Zone ist eine Ablage mit
 * Einfuegelinie. Gezogen wird mit dem Browser selbst (HTML5 Drag and Drop),
 * ohne Bibliothek. Tastatur: alle Aktionen sind Knoepfe mit Beschriftung.
 */

const FrameButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }> = ({ label, className = '', children, ...rest }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        {...rest}
        className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 [&>svg]:w-3.5 [&>svg]:h-3.5 ${className}`}
    >
        {children}
    </button>
);

/** Kleines Menue „In Zone verschieben" — schliesst mit Esc, Klick daneben oder Auswahl. */
const ZoneMenu: React.FC<{ title: string; zone: Zone; onMoveToZone: (zone: Zone) => void }> = ({ title, zone, onMoveToZone }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const root = rootRef.current;
        root?.querySelector<HTMLButtonElement>('[role="menuitemradio"]:not(:disabled)')?.focus();
        const onDown = (e: PointerEvent) => { if (root && !root.contains(e.target as Node)) setOpen(false); };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); setOpen(false); buttonRef.current?.focus(); return; }
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            const items = Array.from(root?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]:not(:disabled)') ?? []);
            if (items.length === 0) return;
            e.preventDefault();
            const i = items.indexOf(document.activeElement as HTMLButtonElement);
            items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
        };
        // Capture-Phase: Esc soll hier nur das Menue schliessen, nicht den
        // Anpassen-Modus — dessen Listener (Bubble) sieht dann defaultPrevented.
        document.addEventListener('pointerdown', onDown);
        document.addEventListener('keydown', onKey, { capture: true });
        return () => {
            document.removeEventListener('pointerdown', onDown);
            document.removeEventListener('keydown', onKey, { capture: true });
        };
    }, [open]);

    return (
        <div ref={rootRef} className="relative">
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`„${title}“ in eine andere Zone verschieben – jetzt: ${ZONE_SHORT[zone]}`}
                title="In eine andere Zone verschieben"
                onClick={() => setOpen(v => !v)}
                className="inline-flex items-center gap-1 h-7 px-1.5 rounded-md text-[11px] font-semibold text-slate-600 hover:bg-slate-200/70 hover:text-slate-900"
            >
                <LayoutTemplate className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span className="hidden md:inline">{ZONE_SHORT[zone]}</span>
                <ChevronDown className="w-3 h-3 shrink-0" aria-hidden="true" />
            </button>
            {open && (
                <div role="menu" aria-label="Zone" className="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-lg bg-white py-1 shadow-lg animate-in fade-in zoom-in-95 duration-150">
                    {ZONES.map(z => (
                        <button
                            key={z}
                            type="button"
                            role="menuitemradio"
                            aria-checked={z === zone}
                            disabled={z === zone}
                            onClick={() => { setOpen(false); onMoveToZone(z); }}
                            className="w-full text-left px-3 h-8 text-[12px] flex items-center justify-between gap-3 text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-50 disabled:text-slate-400 disabled:cursor-default disabled:hover:bg-transparent"
                        >
                            {ZONE_LABEL[z]}
                            {z === zone && <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface WidgetFrameProps {
    id: WidgetId;
    zone: Zone;
    canUp: boolean;
    canDown: boolean;
    /** Genau dieser Baustein haengt gerade am Zeiger. */
    dragging: boolean;
    onDragStart: (id: WidgetId) => void;
    onDragEnd: () => void;
    onNudge: (id: WidgetId, delta: -1 | 1) => void;
    onMoveToZone: (id: WidgetId, zone: Zone) => void;
    onHide: (id: WidgetId) => void;
    children: React.ReactNode;
}

/**
 * Rahmen um einen platzierten Baustein. Der Inhalt bleibt sichtbar, ist aber
 * nicht bedienbar, gedeckelt und laeuft unten aus — so bleibt die Seite beim
 * Anordnen ueberschaubar.
 */
export const WidgetFrame: React.FC<WidgetFrameProps> = ({ id, zone, canUp, canDown, dragging, onDragStart, onDragEnd, onNudge, onMoveToZone, onHide, children }) => {
    const headRef = useRef<HTMLDivElement>(null);
    const info = WIDGET_BY_ID[id];

    const start = (e: React.DragEvent) => {
        setDragPayload(e.dataTransfer, id);
        if (headRef.current) e.dataTransfer.setDragImage(headRef.current, 20, 20);
        // Erst nach dem Start den Zustand aendern — aendert sich das Element
        // noch waehrend dragstart, bricht Chrome das Ziehen ab.
        window.setTimeout(() => onDragStart(id), 0);
    };

    return (
        <div data-widget-frame={id} className={`rounded-xl border bg-white transition-opacity ${dragging ? 'opacity-40 border-blue-300' : 'border-slate-300'}`}>
            <div ref={headRef} className="flex items-center gap-1 pl-1 pr-1.5 h-10 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                <span
                    draggable
                    onDragStart={start}
                    onDragEnd={onDragEnd}
                    title="Ziehen, um zu verschieben"
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 cursor-grab active:cursor-grabbing hover:bg-slate-200/70 hover:text-slate-700"
                >
                    <GripVertical className="w-4 h-4" />
                </span>
                <span className="text-[12px] font-semibold text-slate-800 truncate flex-1 min-w-0">{info.title}</span>
                <FrameButton label={`„${info.title}“ nach oben`} disabled={!canUp} onClick={() => onNudge(id, -1)}><ArrowUp /></FrameButton>
                <FrameButton label={`„${info.title}“ nach unten`} disabled={!canDown} onClick={() => onNudge(id, 1)}><ArrowDown /></FrameButton>
                <ZoneMenu title={info.title} zone={zone} onMoveToZone={z => onMoveToZone(id, z)} />
                <FrameButton label={`„${info.title}“ ausblenden`} onClick={() => onHide(id)}><EyeOff /></FrameButton>
            </div>
            <div className="p-2 bg-slate-50/60 rounded-b-xl">
                {/* `inert` statt nur pointer-events-none: sonst erreichte die Tab-Taste die
                    abgeschnittenen Vorschau-Knoepfe, und Enter verschob z. B. einen Termin,
                    waehrend man eigentlich das Layout bearbeitet (Codex-Review 26.09.2026). */}
                <div inert aria-hidden="true" className="max-h-[220px] overflow-hidden pointer-events-none select-none opacity-75 [mask-image:linear-gradient(to_bottom,#000_60%,transparent_100%)]">
                    {children}
                </div>
            </div>
        </div>
    );
};

/** Abstand der Rahmen in einer Zone (gap-3) — fuer die Lage der Einfuegelinie. */
const GAP = 12;

interface ZoneAreaProps {
    zone: Zone;
    /** Anzahl platzierter Bausteine (fuer „leer" und den Ablage-Index am Ende). */
    count: number;
    /** Baustein am Zeiger, egal ob aus einer Zone oder aus der Ablage. */
    dragging: WidgetId | null;
    onDrop: (id: WidgetId, zone: Zone, index: number) => void;
    children: React.ReactNode;
    className?: string;
}

/**
 * Ablage einer Zone. Waehrend des Ziehens zeigt eine Linie, zwischen welchen
 * Nachbarn der Baustein landet; eine leere Zone bietet sich als Ganzes an.
 */
export const ZoneArea: React.FC<ZoneAreaProps> = ({ zone, count, dragging, onDrop, children, className = '' }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [hover, setHover] = useState<{ index: number; y: number | null } | null>(null);

    // Endet oder wechselt das Ziehen, ist die Linie hinfaellig — beim Rendern
    // zurueckgesetzt (React-Muster fuer Zustand, der von einer Prop abhaengt).
    const [seenDragging, setSeenDragging] = useState(dragging);
    if (seenDragging !== dragging) {
        setSeenDragging(dragging);
        setHover(null);
    }

    const measure = (clientY: number) => {
        const el = ref.current;
        if (!el) return { index: count, y: null };
        const frames = Array.from(el.querySelectorAll<HTMLElement>(':scope > [data-widget-frame]')).filter(f => f.dataset.widgetFrame !== dragging);
        const bands = frames.map(f => { const r = f.getBoundingClientRect(); return { top: r.top, bottom: r.bottom }; });
        const index = insertionIndex(bands.map(b => (b.top + b.bottom) / 2), clientY);
        return { index, y: insertionLineY(bands, index, el.getBoundingClientRect().top, GAP) };
    };

    const onDragOver = (e: React.DragEvent) => {
        if (!dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const next = measure(e.clientY);
        setHover(h => (h && h.index === next.index && h.y === next.y) ? h : next);
    };

    const onDragLeave = (e: React.DragEvent) => {
        const to = e.relatedTarget as Node | null;
        if (!to || !ref.current?.contains(to)) setHover(null);
    };

    const onDropHere = (e: React.DragEvent) => {
        e.preventDefault();
        const id = dragging ?? readDragPayload(e.dataTransfer);
        const index = hover?.index ?? count;
        setHover(null);
        if (id) onDrop(id, zone, index);
    };

    const active = hover !== null;
    return (
        <div
            ref={ref}
            data-zone={zone}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDropHere}
            className={`relative rounded-xl p-2 -m-2 flex flex-col gap-3 transition-colors ${active ? 'bg-blue-50/70' : ''} ${className}`}
        >
            {active && hover.y !== null && (
                <div aria-hidden="true" className="absolute left-2 right-2 h-0.5 rounded-full bg-blue-600 pointer-events-none" style={{ top: hover.y - 1 }} />
            )}
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-blue-500' : 'bg-slate-300'}`} aria-hidden="true" />
                {ZONE_LABEL[zone]}
            </div>
            {children}
            {count === 0 && (
                <div className={`rounded-xl border border-dashed min-h-[88px] flex items-center justify-center text-[11px] font-semibold ${active ? 'border-blue-400 text-blue-700' : 'border-slate-300 text-slate-500'}`}>
                    Baustein hierher ziehen
                </div>
            )}
        </div>
    );
};
